/**
 * "Your trusts" — the cross-firm role list on the profile screen.
 *
 * Beno, 2026-08-18: "When I put my ID number in and I do my FICA, then it will
 * show where am I the founder, where am I a trustee, and on which one I will be
 * a beneficiary." He had opened his profile and NOT seen a trust Waldo had
 * registered — "we're a little bit in a silo yet". Paul's ruling: it belongs
 * under "your trusts", grouped by role.
 *
 * ── The two kinds of row ─────────────────────────────────────────────────────
 * A row you can open is a link. A row you cannot is deliberately NOT a link and
 * NOT an error — it is the honest statement "your name is in this deed and
 * nobody has given you access yet". That row is where a future "claim this
 * trust" action lives; the space and the copy are laid out for it, and the
 * action itself is not built here (the claim flow has its own approval path).
 *
 * ── The empty states are the hard part ───────────────────────────────────────
 * Three different things produce zero rows and the screen must not blur them:
 * we have not verified who you are · you have no SA ID on file · you genuinely
 * appear on no trusts. Only the last may say "no trusts". Getting this wrong
 * would tell a founder their trust does not exist.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '../icon';
import { useMyTrustRoles, groupRolesByKind, type TrustRoleView } from '../../lib/api';
import { displayTrustName } from '../../lib/format/trust-name';
import { mapError } from '../../lib/error-messages';
import { ClaimTrustDialog } from './claim-trust-dialog';

function formatSince(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}

const rowBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 0',
  borderBottom: '1px solid var(--hairline)',
};

function RoleRow({ row, last, onAskForAccess }: {
  row: TrustRoleView;
  last: boolean;
  /** Only ever called for a row with no access — the button does not exist
   *  on a trust you can already open. */
  onAskForAccess?: (row: TrustRoleView) => void;
}) {
  const since = formatSince(row.addedAt);
  const style: React.CSSProperties = {
    ...rowBase,
    ...(last ? { borderBottom: 'none' } : {}),
  };

  const body = (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="serif"
          style={{
            fontSize: 15,
            color: 'var(--ink)',
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayTrustName(row.trustName)}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
          {row.hasAccess
            ? since
              ? `On record since ${since}`
              : 'On record'
            : /* The whole point of the feature: the trust is REAL, we found the
                 person's name in it, and they simply have not been let in. Say
                 that, rather than hiding the row or implying a fault. */
              'Not shared with you yet'}
        </div>
      </div>
      {row.hasAccess ? (
        <Icon name="arrow-right" size={16} color="var(--ink-3)" />
      ) : (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAskForAccess?.(row); }}
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.01em',
            padding: '3px 10px',
            borderRadius: 999,
            background: 'var(--accent-50)',
            border: '1px solid var(--accent-600)',
            color: 'var(--accent-700, var(--ink))',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          Ask for access
        </button>
      )}
    </>
  );

  // No link, no cursor, no hover on a trust that cannot be opened. An
  // affordance that does nothing is the defect Beno reported on a different
  // screen ("Don't work" — 2026-07-30); do not reintroduce it here.
  return row.hasAccess ? (
    <Link
      to="/app/trusts/$id"
      params={{ id: row.trustId }}
      style={{ ...style, textDecoration: 'none', color: 'inherit' }}
    >
      {body}
    </Link>
  ) : (
    <div style={style}>{body}</div>
  );
}

export function YourTrustsCard() {
  const q = useMyTrustRoles();
  // The row someone clicked "Ask for access" on.
  //
  // ⚠️ The dialog CANNOT be prefilled with the registration number: the person
  // index carries tenantId / trustId / trustName / role and no registration, so
  // showing one would mean looking it up and handing it back to someone who has
  // not been let in. They type it — which is the thing they demonstrably know.
  const [claiming, setClaiming] = useState<TrustRoleView | null>(null);

  return (
    <div className="card" data-testid="your-trusts-card">
      <div className="card-head" style={{ marginBottom: 16 }}>
        <div>
          <div className="eyebrow">Across every firm</div>
          <h3 className="card-title">Your trusts</h3>
        </div>
        {q.data && q.data.roles.length > 0 ? (
          <span className="card-meta">
            {q.data.roles.length} {q.data.roles.length === 1 ? 'role' : 'roles'}
          </span>
        ) : null}
      </div>

      {q.isPending ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }} aria-busy="true">
          Looking for your name&hellip;
        </p>
      ) : q.isError ? (
        // The backend returns 503 rather than an empty list when the index or
        // its key is unavailable, precisely so this never renders a confident
        // "no trusts" over an infrastructure fault.
        <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6 }}>
          We couldn&rsquo;t check your trusts right now. Your data is safe.{' '}
          {mapError(q.error, 'load')}
        </p>
      ) : q.data?.status === 'identity-not-verified' ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6, maxWidth: '58ch' }}>
          Complete your FICA identity verification and this will show every trust your name
          appears in, across every firm on Elreign: where you are the founder, where you are a
          trustee, and where you are a beneficiary.
        </p>
      ) : q.data?.status === 'no-sa-id' ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6, maxWidth: '58ch' }}>
          We match trusts on your South African ID number, and we don&rsquo;t have one on file
          for you. Trusts you hold on a passport won&rsquo;t appear here yet.
        </p>
      ) : q.data && q.data.roles.length === 0 ? (
        // The ONLY branch entitled to say this.
        <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6, maxWidth: '58ch' }}>
          Your name doesn&rsquo;t appear on any trust registered with Elreign yet. Trusts appear
          here as their deeds are read, including ones registered by another firm.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {groupRolesByKind(q.data?.roles ?? []).map((group) => (
            <div key={group.role}>
              <div
                className="eyebrow ink"
                style={{ marginBottom: 6 }}
              >
                {group.label}
              </div>
              {group.rows.map((row, i) => (
                // Keyed on trust AND role: a founder who is also a trustee has
                // two legitimate rows for one trust, in two different groups.
                <RoleRow
                  key={`${row.tenantId}:${row.trustId}:${row.role}`}
                  row={row}
                  onAskForAccess={setClaiming}
                  last={i === group.rows.length - 1}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Mounted only while open: the dialog owns a mutation hook, and a hook
          instantiated for a closed dialog is both wasted and a dependency every
          consumer's tests inherit. */}
      {claiming !== null && (
      <ClaimTrustDialog
        open
        onClose={() => setClaiming(null)}
        // The index knows founder / trustee / beneficiary, but a claim may only
        // ask for trustee or beneficiary. A founder claims as a trustee:
        // "founder" is a historical fact about the deed, not a standing right
        // to read the file.
        initialTier={claiming?.role === 'beneficiary' ? 'beneficiary' : 'trustee'}
      />
      )}
    </div>
  );
}

/**
 * Standalone entry — "I know the number of a trust I should be on".
 *
 * Deliberately NOT only on a matched row. The person index is keyed on a
 * FICA-verified ID number and no KYC vendor is wired yet, so the card above
 * currently reads "we have not confirmed who you are" for every user. A claim
 * action that lived only on a matched row would therefore be unreachable by
 * anybody — the feature would ship dead without a single line of it being
 * wrong, which is the failure this codebase keeps repeating.
 */
export function ClaimATrustEntry({ fullName }: { fullName?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => setOpen(true)}
        data-testid="claim-a-trust"
      >
        Ask for access to a trust
      </button>
      {open && (
        <ClaimTrustDialog open onClose={() => setOpen(false)} initialFullName={fullName ?? ''} />
      )}
    </>
  );
}
